import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'lib/axios'
import clsx from 'clsx'
import { useDropdown } from 'components/utility/hooks/useDropdown'

const CONTACT_TYPES = [
  { value: 'purchaser',       label: 'The Purchaser/Transferee' },
  { value: 'purchaser_agent', label: "The Purchaser/Transferee's Agent" },
]

const LEGAL_COPY =
  'I certify I am the Purchaser or have been given explicit right to act on behalf of the ' +
  'Purchaser to provide preferred communication delivery addresses. By checking this box, ' +
  'I attest that the information provided is true and accurate.'

function entryToFields(entry) {
  if (!entry) {
    return { contact_type: '', full_name: '', email: '', address_line1: '', address_line2: '', city: '', state: '', postal_code: '' }
  }
  return {
    contact_type:  entry.contact_type           || '',
    full_name:     entry.full_name              || '',
    email:         entry.email                  || '',
    address_line1: entry.address?.line1         || '',
    address_line2: entry.address?.line2         || '',
    city:          entry.address?.city          || '',
    state:         entry.address?.state         || '',
    postal_code:   entry.address?.postal_code   || '',
  }
}

const DropdownSelect = ({ dealParties, onSelect, initialPartyId, packageHashedId }) => {
  const initialParty = initialPartyId ? dealParties.find(dp => dp.id === initialPartyId) : null
  const [selected, setSelected] = useState(
    initialParty ? { label: initialParty.display_name, value: initialParty.id } : { label: 'select from deal parties', value: null }
  )
  const { ref, show, close, toggle} = useDropdown()

  const handleSelect = (option) => {
    setSelected(option)
    onSelect(option.value)
    close()
  }

  return (
    <div ref={ref} className='compliance-form-dropdown-select dome-relative'>
      <div className='compliance-form-selected dome-dark-grey-border dome-padding6-12 dome-rounded-border dome-d-flex dome-align-center dome-justify-between' onClick={toggle}>
        {selected.label}
        <i className={clsx('fal fa-angle-down', show && 'fa-rotate-180')} />
      </div>
      {show && <div className='compliance-form-options dome-scrollable dome-d-flex dome-flex-column dome-dark-shadow dome-rounded-border dome-bg-white dome-absolute dome-full-width'>
        {dealParties.map((dp) => (
          <div key={dp.id} className='compliance-form-option' onClick={() => handleSelect({ label: dp.display_name, value: dp.id })}>
            <div className='compliance-form-option-inner-wrap'>
              <div className='dome-p1 dome-color-dark-grey'>{dp.display_name}</div>
              <div className='dome-p3 dome-color-med-grey'>{dp.role}</div>
            </div>
          </div>
        ))}
        <div className='compliance-form-dropdown-select-footer dome-padding6-12'>
          <span className='dome-color-med-grey'>
            missing someone? first add them to {" "}
            <a href={`/packages/${packageHashedId}/overview`} className='compliance-form-multiselect-link'>
              deal parties
            </a>
          </span>
        </div>
      </div>}
    </div>
  )
}

const MultiSelectDropdown = ({ dealParties, onSelect, isItemDisabled = () => false, disabled, packageHashedId, initialIds = [] }) => {
  const [selected, setSelected] = useState(initialIds)
  const { ref, show, close, toggle } = useDropdown()

  const toggleItem = (id) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]
    setSelected(next)
    onSelect(next)
  }

  const removePill = (id) => {
    const next = selected.filter(x => x !== id)
    setSelected(next)
    onSelect(next)
  }

  const confirmedParties = dealParties.filter(dp => selected.includes(dp.id))

  return (
    <div ref={ref} className='compliance-form-multiselect dome-relative dome-d-flex dome-flex-column dome-gap6 dome-full-width'>
      <div className={clsx('compliance-form-selected dome-padding6-12 dome-rounded-border dome-color-lite-grey dome-d-flex dome-align-center dome-justify-between', disabled ? 'dome-disabled-border dome-cursor-not-allowed' : 'dome-dark-grey-border')} onClick={disabled ? undefined : toggle}>
        select from deal parties
        <i className={clsx('fal fa-angle-down dome-color-darkest-grey', show && 'fa-rotate-180')} />
      </div>
      {confirmedParties.length > 0 && (
        <div className='compliance-form-multiselect-pills dome-d-flex dome-flex-wrap dome-gap6'>
          {confirmedParties.map(dp => (
            <div key={dp.id} className='compliance-form-multiselect-pill dome-d-flex dome-align-center dome-gap9 dome-padding3-6 dome-rounded-border dome-darkest-grey-border dome-bg-fill'>
              <span>{dp.role} - {dp.display_name}</span>
              <button type="button" className='dome-btn dome-btn-link dome-btn-link-med-grey' onClick={() => removePill(dp.id)}>
                <i className='fal fa-xmark' />
              </button>
            </div>
          ))}
        </div>
      )}
      {show && (
        <div className="compliance-form-multiselect-wrapper dome-absolute dome-full-width dome-dark-shadow dome-rounded-border dome-bg-white">
          <div className='compliance-form-multiselect-options dome-scrollable dome-d-flex dome-flex-column dome-bg-white'>
            {dealParties.map(dp => {
              const disabled = isItemDisabled(dp)
              return (
                <div
                  key={dp.id}
                  className={clsx('compliance-form-option dome-d-flex dome-align-baseline dome-gap6', { 'compliance-form-option--disabled': disabled })}
                  onClick={() => !disabled && toggleItem(dp.id)}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(dp.id)}
                    disabled={disabled}
                    onChange={() => toggleItem(dp.id)}
                    onClick={e => e.stopPropagation()}
                  />
                  <div className='compliance-form-option-inner-wrap'>
                    <div className='dome-p1'>{dp.display_name}</div>
                    <div className='dome-p3 dome-color-med-grey'>{dp.role}</div>
                  </div>
                </div>
              )
            })}
          </div>
          <div className='compliance-form-multiselect-footer dome-d-flex dome-align-center dome-justify-between dome-padding6-12'>
              <div className='dome-color-med-grey'>
                missing someone? first add them to {" "}
                <a href={`/packages/${packageHashedId}/overview`} className='compliance-form-multiselect-link'>
                  deal parties
                </a>
              </div>
              <div className='compliance-form-multiselect-actions dome-d-flex dome-align-center dome-gap12'>
                <span className='dome-p2 dome-color-lite-grey'>{selected.length} selected</span>
                <button type="button" className='dome-btn dome-btn-base dome-btn-aqua-stroke' onClick={close}>
                  done
                </button>
              </div>
            </div>
        </div>
      )}
    </div>
  )
}

export default function Editor({ packageHashedId, dealParties, currentEntry, onSaved, onCancel }) {
  const initialPartyId   = currentEntry ? (dealParties.find(dp => dp.email === currentEntry.email)?.id ?? null) : null
  const initialExtraIds  = currentEntry ? dealParties.filter(dp => (currentEntry.additional_emails || []).includes(dp.email)).map(dp => dp.id) : []
  const [partyId,     setPartyId]     = useState(initialPartyId)
  const [extraEmails, setExtraEmails] = useState(currentEntry?.additional_emails || [])
  const [error,       setError]       = useState(null)
  const [editors,     setEditors]     = useState([])
  const [submitting,  setSubmitting]  = useState(false)

  const { register, handleSubmit, setValue, watch, formState: { errors, isValid } } = useForm({
    mode: 'onChange',
    defaultValues: { ...entryToFields(currentEntry), attested: false },
  })

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `/package_hook/${packageHashedId}/ny_1120b/compliance_form/active_editors`
        )
        setEditors(data.editors || [])
      } catch {}
    }, 30_000)
    return () => clearInterval(interval)
  }, [packageHashedId])

  function pickParty(id) {
    const dp = dealParties.find(d => d.id === id)
    if (!dp) return
    setPartyId(id)
    setValue('full_name',     dp.display_name)
    setValue('email',         dp.email || '')
    setValue('address_line1', dp.parsed_address?.line1       || '')
    setValue('address_line2', '')
    setValue('city',          dp.parsed_address?.city        || '')
    setValue('state',         dp.parsed_address?.state       || '')
    setValue('postal_code',   dp.parsed_address?.postal_code || '')
  }

  async function onSubmit(data) {
    setSubmitting(true)
    setError(null)
    try {
      const { data: responseData } = await axios.post(
        `/package_hook/${packageHashedId}/ny_1120b/compliance_form`,
        {
          contact_type:        data.contact_type,
          full_name:           data.full_name,
          address_line1:       data.address_line1,
          address_line2:       data.address_line2,
          city:                data.city,
          state:               data.state,
          postal_code:         data.postal_code,
          email:               data.email,
          additional_emails:   extraEmails,
          authority_confirmed: data.attested,
          known_submitted_at:  currentEntry?.submitted_at || null,
        }
      )
      onSaved(responseData.entry, responseData)
    } catch (err) {
      const errCode = err.response?.data?.error
      if (errCode === 'already_completed_or_updated') {
        setError('This form was already updated by someone else. Please refresh and try again.')
      } else if (errCode === 'not_available') {
        setError('This form can no longer be edited. Please refresh to see the current version.')
      } else if (err.response?.data?.errors) {
        const msgs = Object.values(err.response.data.errors).flat().join(', ')
        setError(msgs)
      } else {
        setError('Submission failed. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const disabled     = !partyId
  const contactType  = watch('contact_type')
  const currentEmail = watch('email')
  const attested     = watch('attested')

  return (
    <div className="compliance-form-editor">
      <div className="compliance-form-divider"></div>
      {editors.length > 0 && (
        <div className="compliance-form-editor-banner">
          {editors.map(e => e.name).join(', ')}{' '}
          {editors.length === 1 ? 'is' : 'are'} currently editing this form.
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="compliance-form-contact-type">
          <div className='dome-d-flex dome-flex-column dome-gap6' style={{maxWidth: 564}}>
            <div className='dome-p1 dome-text-w500'>Please mail the acknowledgment/notice required under law to <span className='dome-text-w400'>(select one)</span>:</div>
              <div className='dome-form-group'>
                {CONTACT_TYPES.map(ct => (
                  <div key={ct.value} className='dome-form-control dome-gap12'>
                    <input
                      id={ct.value}
                      type="radio"
                      value={ct.value}
                      {...register('contact_type', { required: 'required' })}
                    />
                    <label className='dome-color-dark-grey dome-p1' htmlFor={ct.value}>{ct.label}</label>
                  </div>
                ))}
              </div>
              {errors.contact_type && <span className='dome-color-red-alert dome-p3'>{errors.contact_type.message}</span>}
          </div>
        </div>

        {contactType && <>
          <div className='compliance-form-purchaser-agent-info dome-d-flex dome-flex-column dome-gap6'>
            <div className='dome-p1 dome-text-w500'>Purchaser/Transferee's {contactType === 'purchaser_agent' ? 'Agent' : ''} Information</div>
            <div className='dome-form-group'>
              <div className='dome-form-control'>
                <DropdownSelect dealParties={dealParties} onSelect={pickParty} initialPartyId={initialPartyId} packageHashedId={packageHashedId} />
              </div>
            </div>
            <div className='dome-form-group'>
              <div className='dome-form-control'>
                <label className='dome-color-med-grey dome-d-flex dome-align-center dome-gap6' htmlFor="full_name">full name* {errors.full_name && <span className='dome-color-red-alert dome-p3'>{errors.full_name.message}</span>}</label>
                <input id="full_name" {...register('full_name', { required: 'full name required' })} disabled={disabled} />
              </div>
            </div>
            <div className='dome-form-group'>
              <div className='dome-form-control'>
                <label className='dome-color-med-grey dome-d-flex dome-align-center dome-gap6' htmlFor="address_line1">address line 1* {errors.address_line1 && <span className='dome-color-red-alert dome-p3'>{errors.address_line1.message}</span>}</label>
                <input id="address_line1" {...register('address_line1', { required: 'address required' })} disabled={disabled} />
              </div>
              <div className='dome-form-control'>
                <label className='dome-color-med-grey' htmlFor="address_line2">address line 2</label>
                <input id="address_line2" {...register('address_line2')} disabled={disabled} />
              </div>
            </div>
            <div className='dome-form-group'>
              <div className='dome-form-control'>
                <label className='dome-color-med-grey dome-d-flex dome-align-center dome-gap6' htmlFor="city">city* {errors.city && <span className='dome-color-red-alert dome-p3'>{errors.city.message}</span>}</label>
                <input id="city" {...register('city', { required: 'city required' })} disabled={disabled} />
              </div>
              <div className='dome-form-control'>
                <label className='dome-color-med-grey dome-d-flex dome-align-center dome-gap6' htmlFor="state">state* {errors.state && <span className='dome-color-red-alert dome-p3'>{errors.state.message}</span>}</label>
                <input id="state" {...register('state', { required: 'state required' })} disabled={disabled} />
              </div>
              <div className='dome-form-control'>
                <label className='dome-color-med-grey dome-d-flex dome-align-center dome-gap6' htmlFor="postal_code">zip code* {errors.postal_code && <span className='dome-color-red-alert dome-p3'>{errors.postal_code.message}</span>}</label>
                <input id="postal_code" {...register('postal_code', { required: 'zip code required' })} disabled={disabled} />
              </div>
            </div>
            {/* email is always read-only — sourced from the selected deal party */}
            <div className='dome-form-group'>
              <div className='dome-form-control compliance-form-email'>
                <label className={clsx(disabled ? 'dome-color-disabled' : 'dome-color-med-grey')} htmlFor="email">email address</label>
                <input id="email" {...register('email')} disabled={disabled} readOnly />
              </div>
            </div>
          </div>
          <div className="compliance-form-additional-emails dome-d-flex dome-align-baseline dome-gap12">
            <i className='fas fa-circle-plus dome-color-aqua' />
            <div className='dome-flex-1'>
              <div className="compliance-form-section-label dome-p1 dome-color-aqua">add additional email recipients</div>
              <div className='dome-color-dark-grey'>Recipients will receive an email notification for all formal correspondence regarding this application, including acknowledgment of materials with any deficiencies and board decisions.</div>
              <MultiSelectDropdown
                key={partyId}
                packageHashedId={packageHashedId}
                dealParties={dealParties.filter(dp => dp.email !== currentEmail)}
                disabled={disabled}
                isItemDisabled={dp => !dp.email}
                initialIds={initialExtraIds}
                onSelect={ids => setExtraEmails(ids.map(id => dealParties.find(dp => dp.id === id)?.email).filter(Boolean))}
              />
            </div>
          </div>
          <div className='compliance-form-divider'></div>
          <div className="dome-form-control dome-gap6 dome-align-baseline">
            <input
              id="attested"
              type="checkbox"
              disabled={disabled}
              {...register('attested', { required: 'please attest before submitting' })}
            />
            <label htmlFor="attested" className="compliance-form-attestation">{LEGAL_COPY} {errors.attested && <span className='dome-color-red-alert dome-p3'>{errors.attested.message}</span>}</label>
          </div>
          {error && <div className="compliance-form-error">{error}</div>}
          <div className="compliance-form-actions dome-d-flex dome-align-center dome-justify-between">
            <button type="button" onClick={onCancel} className="dome-btn dome-btn-link dome-btn-link-cancel">
              cancel
            </button>
            <button type="submit" disabled={submitting} className={clsx('dome-btn dome-btn-base', !partyId || !isValid || submitting ? 'dome-btn-disabled' : 'dome-btn-dark-green')}>
              { submitting ? <i className='fa-duotone fa-pulse fa-loader' /> : <span>confirm</span>}
            </button>
          </div>
        </>}
      </form>
    </div>
  )
}
